import { useEffect, useRef, useState } from "react";

/**
 * Animate a numeric portion of a string when scrolled into view.
 * Example values: "£29", "£249", "current", "2.1×", "31"
 * The first numeric token (int or decimal) is animated; everything else
 * (currency symbol, suffix like "×", version dots) is kept verbatim.
 */
export default function useCountUp(value, { duration = 1400 } = {}) {
    const ref = useRef(null);
    const [display, setDisplay] = useState(() => value);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const str = String(value);
        // Match the first number (int or decimal) anywhere in the string
        const match = str.match(/(\d+(?:\.\d+)?)/);
        if (!match) {
            setDisplay(str);
            return;
        }
        const target = parseFloat(match[1]);
        const decimals = (match[1].split(".")[1] || "").length;
        const prefix = str.slice(0, match.index);
        const suffix = str.slice(match.index + match[1].length);

        let started = false;
        let rafId;
        const animate = (ts, start) => {
            const t = Math.min(1, (ts - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
            const current = target * eased;
            const formatted = decimals
                ? current.toFixed(decimals)
                : Math.round(current).toLocaleString();
            setDisplay(`${prefix}${formatted}${suffix}`);
            if (t < 1) {
                rafId = requestAnimationFrame((next) => animate(next, start));
            } else {
                setDisplay(str);
            }
        };

        // Start at 0 immediately so the value doesn't flash full before observe
        setDisplay(`${prefix}${(0).toFixed(decimals)}${suffix}`);

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !started) {
                        started = true;
                        const startTs = performance.now();
                        rafId = requestAnimationFrame((ts) =>
                            animate(ts, startTs),
                        );
                        obs.disconnect();
                    }
                });
            },
            { threshold: 0.4 },
        );
        obs.observe(el);

        return () => {
            obs.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [value, duration]);

    return [ref, display];
}
